import { useState, useCallback } from 'react';
import { FolderData } from '../types';
import clioDocumentPickerService from '~/src/services/api/clioDocumentPickerService';
import { useCurrentOrg } from '~/src/entities/user/hooks/useCurrentOrg';
import { transformFoldersToFolderData } from './helpers/hookHelpers';

/**
 * Hook for managing pagination (scroll-based) of matter folder's children folders
 */
export const useRootFolderPagination = (root_folder_id: number) => {
  const [items, setItems] = useState<FolderData[]>([]);
  const [hasInitialData, setHasInitialData] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | undefined | null>(
    undefined,
  );
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const { org } = useCurrentOrg();

  const PAGINATION_LIMIT = 25;

  const loadNext = useCallback(async (): Promise<void> => {
    if (isLoading || !hasMoreData) return;

    setIsLoading(true);
    try {
      if (!org?.fprint) {
        throw new Error('Organization fprint is not set');
      }

      const apiResponse = await clioDocumentPickerService.listFolders(
        org.fprint,
        {
          parent_id: root_folder_id,
          limit: PAGINATION_LIMIT,
          page_token: nextPageToken || undefined,
        },
      );

      let newItems: FolderData[] = [];
      if (apiResponse.folders) {
        newItems = transformFoldersToFolderData(
          apiResponse.folders,
          1,
          root_folder_id,
        );
      }

      setItems((currentItems) => [...currentItems, ...newItems]);

      if (apiResponse.next_page_token) {
        setHasMoreData(true);
        setNextPageToken(apiResponse.next_page_token);
      } else {
        setHasMoreData(false);
        setNextPageToken(null);
      }
    } catch (error) {
      console.error('Failed to load folders:', error);
      setHasLoadingError(true);
    } finally {
      setIsLoading(false);
      if (!hasInitialData) {
        setHasInitialData(true);
      }
    }
  }, [
    root_folder_id,
    org?.fprint,
    hasInitialData,
    nextPageToken,
    hasMoreData,
    isLoading,
  ]);

  return {
    items,
    hasInitialData,
    hasMoreData,
    isLoading,
    hasLoadingError,
    loadNext,
  };
};
